import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Search, X, ChevronLeft, RefreshCw, Bell, KeyRound } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useCurrency } from '../../context/CurrencyContext';
import CurrencySelector from '../common/CurrencySelector';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { MintedLogoHorizontal } from '../common/MintedLogo';

const Header = ({ title, showBack = false, showSearch = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout, switchRole, unreadNotificationsCount } = useApp();
  const { currency } = useCurrency();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const isSeller = user?.role === 'seller';

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/browse?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'Categories', path: '/categories' },
    { label: 'Services', path: '/services' },
    { label: 'Notifications', path: '/notifications' },
    { label: 'My Listings', path: '/my-listings' },
    { label: 'Saved Searches', path: '/saved-searches' },
    { label: 'My Orders', path: '/orders' },
    { label: 'Offers', path: '/offers' },
    { label: 'Messages', path: '/messages' },
    { label: 'Wallet', path: '/wallet' },
    { label: 'Profile', path: '/profile' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-stone-200/60 z-50 safe-area-top">
      <div className="max-w-lg mx-auto flex items-center justify-between h-14 px-4">
        {/* Left side */}
        <div className="flex items-center gap-2">
          {showBack ? (
            <button
              onClick={() => navigate(-1)}
              className="p-1 -ml-1 hover:bg-stone-100 rounded-sm transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-stone-900" strokeWidth={1.5} />
            </button>
          ) : (
            <Sheet>
              <SheetTrigger asChild>
                <button className="p-1 -ml-1 hover:bg-stone-100 rounded-sm transition-colors">
                  <Menu className="w-5 h-5 text-stone-900" strokeWidth={1.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 flex flex-col h-full">
                <div className="bg-minted-900 p-6 flex-shrink-0">
                  <span className="font-display text-xl font-medium text-white tracking-tight">Minted</span>
                  <p className="text-minted-400 text-[10px] tracking-widest uppercase mt-0.5">Marketplace</p>
                  {isAuthenticated && user && (
                    <div className="mt-5">
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={user.avatar}
                          alt={user.name}
                          className="w-10 h-10 rounded-sm object-cover border border-minted-700"
                        />
                        <div>
                          <p className="text-white font-medium text-sm">{user.name}</p>
                          <p className="text-minted-300 text-xs capitalize">{user.role} Mode</p>
                        </div>
                      </div>
                      <button
                        onClick={switchRole}
                        className="w-full flex items-center justify-center gap-2 border border-minted-600 text-minted-200 hover:text-white hover:border-minted-400 rounded-sm px-3 py-2 text-sm transition-colors"
                      >
                        <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                        Switch to {isSeller ? 'Buyer' : 'Seller'} Mode
                      </button>
                    </div>
                  )}
                </div>
                <nav className="p-4 flex-1 overflow-y-auto pb-10" data-testid="side-menu-nav">
                  {menuItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`w-full text-left px-4 py-2.5 rounded-sm mb-0.5 transition-colors text-sm ${
                        location.pathname === item.path
                          ? 'bg-minted-50 text-minted-800 font-medium'
                          : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                  <div className="border-t border-stone-200 mt-4 pt-4">
                    <button
                      onClick={() => navigate('/private-collection')}
                      className="w-full text-left px-4 py-3 rounded-sm mb-1 transition-colors group"
                      style={{ backgroundColor: '#14100e', border: '1px solid rgba(201,169,98,0.3)' }}
                      data-testid="menu-private-collection"
                    >
                      <div className="flex items-center gap-2">
                        <KeyRound className="w-3.5 h-3.5 shrink-0" style={{ color: '#c9a962' }} strokeWidth={1.3} />
                        <span className="text-sm" style={{ fontFamily: 'Georgia, serif', color: '#f0e9df' }}>The Private Collection</span>
                        <span className="text-[9px] tracking-[0.2em] uppercase ml-auto" style={{ color: 'rgba(201,169,98,0.8)' }}>18+</span>
                      </div>
                      <p className="text-[11px] mt-1 leading-snug" style={{ color: '#a89a8c' }}>
                        18 or over? There may be something here you'd enjoy.
                      </p>
                    </button>
                    {isAuthenticated ? (
                      <button
                        onClick={() => { logout(); navigate('/'); }}
                        className="w-full text-left px-4 py-3 rounded-sm text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors text-sm"
                      >
                        Sign Out
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate('/login')}
                        className="w-full text-left px-4 py-3 rounded-sm text-minted-800 hover:bg-minted-50 transition-colors text-sm font-medium"
                      >
                        Sign In
                      </button>
                    )}
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          )}
        </div>

        {/* Center - Logo/Title */}
        <div className="flex-1 flex items-center justify-center">
          {title ? (
            <h1 className="font-display font-medium text-lg text-stone-900">{title}</h1>
          ) : (
            <button onClick={() => navigate('/')} className="flex items-center">
              <MintedLogoHorizontal size="md" showSubtext={false} />
            </button>
          )}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-1">
          <CurrencySelector compact />
          {isAuthenticated && (
            <>
              <button
                onClick={() => navigate('/notifications')}
                className="p-1.5 hover:bg-stone-100 rounded-sm transition-colors relative"
              >
                <Bell className="w-5 h-5 text-stone-700" strokeWidth={1.5} />
                {unreadNotificationsCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-minted-700 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount}
                  </span>
                )}
              </button>
              <Badge 
                className="text-[10px] cursor-pointer bg-minted-50 text-minted-800 border border-minted-200 hover:bg-minted-100 tracking-wider uppercase rounded-sm"
                onClick={switchRole}
              >
                {isSeller ? 'Selling' : 'Browsing'}
              </Badge>
            </>
          )}
          {showSearch && (
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-1.5 hover:bg-stone-100 rounded-sm transition-colors"
            >
              {searchOpen ? <X className="w-5 h-5 text-stone-700" strokeWidth={1.5} /> : <Search className="w-5 h-5 text-stone-700" strokeWidth={1.5} />}
            </button>
          )}
        </div>
      </div>

      {/* Search bar dropdown */}
      {searchOpen && (
        <div className="bg-white border-t border-stone-100 px-4 py-3">
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input
              type="text"
              placeholder="Search products & services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 border-stone-200 rounded-sm focus:ring-1 focus:ring-minted-700"
              autoFocus
            />
            <Button type="submit" className="bg-minted-800 hover:bg-minted-700 text-white rounded-sm" size="sm">
              Search
            </Button>
          </form>
        </div>
      )}
    </header>
  );
};

export default Header;
